import { ApiClient } from '../../ApiClient';
import { SalesDTO } from '../dto/SalesDTO';
import { SalesForecastsDto } from '../dto/SalesForecastDTO';
import { DailySalesDTO } from '../dto/DailySalesDTO';
import { Resource } from '@domain/base/Resource';
import { PaginatedDTO } from '../../paginated.dto';
import { ProductFilter } from '@domain/product/models/ProductFilter';
import { SalesKpisDTO } from '../dto/SalesKpisDTO';
import { AggregatedQuantityDTO } from '../dto/AggregatedQuantityDTO';
import { SalesKpiOptions } from '@domain/sales/models/SalesKpis';

export class SalesDataSource {
  constructor(private api: ApiClient) { }

  async getSales(
    productIds?: string[],
    startDate?: Date,
    endDate?: Date,
  ): Promise<SalesDTO> {

    const params = {
      product_ids: productIds?.length === 1 ? [productIds[0]] : productIds?.join(','),
      start_date: startDate?.toISOString(),
      end_date: endDate?.toISOString(),
    };

    const response = await this.api.get<SalesDTO>('/sales', { params });
    return response.data;
  }

  async syncSales(connectionId: string, date?: Date): Promise<void> {
    const params = date ? { date: date.toISOString() } : undefined;
    await this.api.post(`/sales/sync/${connectionId}`, null, { params });
  }

  async getForecast(
    startDate?: Date,
    endDate?: Date,
    filterCriteria?: ProductFilter,
  ): Promise<PaginatedDTO<SalesForecastsDto[]>> {
    const params = {
      start_date: startDate?.toISOString(),
      end_date: endDate?.toISOString(),
      ...filterCriteria,
    };

    const response = await this.api.get<PaginatedDTO<SalesForecastsDto[]>>('/sales/forecasts', { params });
    return response.data;
  }

  async runForecast(productId?: string, marketplaceConnectionId?: string, backtest?: boolean): Promise<number> {
    const params = {
      product_id: productId,
      marketplace_connection_id: marketplaceConnectionId,
      backtest: backtest,
    };

    const response = await this.api.post<Resource<void>>('/sales/forecast/run', null, { params });
    return response.status;
  }

  async getDailySales(
    productIds?: string[],
    startDate?: Date,
    endDate?: Date,
  ): Promise<DailySalesDTO[]> {
    const params = {
      start_date: startDate?.toISOString(),
      end_date: endDate?.toISOString(),
      product_ids: productIds?.join(','),
    };

    const response = await this.api.get<DailySalesDTO[]>('/sales/daily', { params });
    return response.data;
  }

  async getWeeklySales(
    startDate?: Date,
    endDate?: Date,
    filterCriteria?: ProductFilter,
    marketplaces?: string[],
    countries?: string[],
  ): Promise<PaginatedDTO<SalesDTO[]>> {
    const params = {
      start_date: startDate?.toISOString(),
      end_date: endDate?.toISOString(),
      ...(marketplaces?.length ? { marketplaces: marketplaces } : {}),
      ...(countries?.length ? { countries: countries } : {}),
      ...filterCriteria,
    };

    const response = await this.api.get<PaginatedDTO<SalesDTO[]>>('/sales/product', { params });
    return response.data;
  }

  async getProductSales(
    productId?: string,
    startDate?: Date,
    endDate?: Date,
    marketplaces?: string[],
    countries?: string[],
  ): Promise<SalesDTO> {
    const params = {
      start_date: startDate?.toISOString(),
      end_date: endDate?.toISOString(),
      ...(marketplaces?.length ? { marketplaces } : {}),
      ...(countries?.length ? { countries } : {}),
    };

    const response = await this.api.get<SalesDTO>(`/sales/product/${productId}`, { params });
    return response.data;
  }

  async getCompanySales(
    startDate?: Date,
    endDate?: Date,
    marketplaces?: string[],
    countries?: string[],
  ): Promise<AggregatedQuantityDTO[]> {
    const params = {
      start_date: startDate?.toISOString(),
      end_date: endDate?.toISOString(),
      ...(marketplaces?.length ? { marketplaces: marketplaces } : {}),
      ...(countries?.length ? { countries: countries } : {}),
    };

    const response = await this.api.get<AggregatedQuantityDTO[]>('/sales/company', { params });
    return response.data;
  }

  async getSalesKpis(options?: SalesKpiOptions): Promise<SalesKpisDTO> {
    const response = await this.api.get<SalesKpisDTO>('/sales/kpis', {
      params: {
        ...(options?.range ? { range: options.range } : {}),
        ...(options?.weeks != null ? { weeks: options.weeks } : {}),
      },
    });
    return response.data;
  }

  async getCompanyForecasts(
    startDate?: Date,
    endDate?: Date,
  ): Promise<AggregatedQuantityDTO[]> {
    const params = {
      start_date: startDate?.toISOString(),
      end_date: endDate?.toISOString(),
    };

    const response = await this.api.get<AggregatedQuantityDTO[]>('/sales/company/forecasts', { params });
    return response.data;
  }

  async updateWeeklySalesAnomalies(productId: string, weeks: { week: number, year: number, anomaly: boolean }[]): Promise<void> {
    await this.api.post(`/sales/anomalies/${productId}`, weeks);
  }

  async clearWeeklySalesAnomalies(productId: string, weeks: { week: number, year: number }[]): Promise<void> {
    await this.api.delete(`/sales/anomalies/${productId}`, { data: weeks });
  }

  async exportSalesCSV(
    startDate?: Date,
    endDate?: Date,
    marketplaces?: string[],
    countries?: string[],
  ): Promise<Blob> {
    const params = {
      start_date: startDate?.toISOString(),
      end_date: endDate?.toISOString(),
      ...(marketplaces?.length ? { marketplaces: marketplaces } : {}),
      ...(countries?.length ? { countries: countries } : {}),
    };

    const response = await this.api.get<Blob>('/sales/export/csv', { 
      params,
      responseType: 'blob'
    });
    return response.data;
  }
} 